// src/services/jozveService.js
// ================================================================
// سرویس خرید جزوه‌های ویژه
// ----------------------------------------------------------------
// • ذخیره‌سازی per-user در سرور (collection: jozveLicenses)
// • ساخت jozveId یکتا برای هر درس
// • بررسی/فعال‌سازی/دریافت خریدها
// ================================================================

import { buildJozveId } from "../data/jozveData";

const API_BASE = '/api/db';

// ── خواندن همه خریدهای یک کاربر از سرور ──
const fetchAllFromServer = async (userId) => {
  if (!userId) return [];
  try {
    const res = await fetch(`${API_BASE}/jozveLicenses?userId=${encodeURIComponent(userId)}`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.warn("⚠️ jozveService: خطا در خواندن از سرور:", e);
    return [];
  }
};

// ── پیدا کردن رکورد یک جزوه مشخص از سرور ──
const fetchOneFromServer = async (userId, jozveId) => {
  if (!userId || !jozveId) return null;
  try {
    const res = await fetch(
      `${API_BASE}/jozveLicenses?userId=${encodeURIComponent(userId)}&jozveId=${encodeURIComponent(jozveId)}`
    );
    const data = await res.json();
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch (e) {
    console.warn("⚠️ jozveService: خطا در خواندن یک رکورد:", e);
    return null;
  }
};

export const jozveService = {
  /** ساخت jozveId از meta */
  makeId({ grade, field, book, chapterNumber, lessonNumber }) {
    return buildJozveId({ grade, field, book, chapterNumber, lessonNumber });
  },

  /** آیا این کاربر این جزوه را خریده؟ (async) */
  async isPurchased(userId, jozveId) {
    if (!userId || !jozveId) return false;
    const found = await fetchOneFromServer(userId, jozveId);
    return !!found;
  },

  /** اطلاعات خرید این جزوه (یا null) — async */
  async getPurchase(userId, jozveId) {
    if (!userId || !jozveId) return null;
    return await fetchOneFromServer(userId, jozveId);
  },

  /** فعال‌سازی خرید + ذخیره در سرور (async) */
  async activatePurchase(userId, jozveId, meta = {}) {
    if (!userId || !jozveId) {
      throw new Error("userId و jozveId الزامی هستند");
    }
    const entry = {
      userId,
      jozveId,
      grade: meta.grade ?? null,
      field: meta.field ?? null,
      book: meta.book ?? null,
      chapterNumber: meta.chapterNumber ?? null,
      lessonNumber: meta.lessonNumber ?? null,
      title: meta.title || "",
      coinPrice: Number(meta.coinPrice) || 0,
      type: meta.type || 'jozve',
      purchasedAt: new Date().toISOString(),
    };
    try {
      const res = await fetch(`${API_BASE}/jozveLicenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      console.warn("⚠️ jozveService: خطا در ذخیره در سرور:", e);
      throw new Error("ذخیره‌سازی در سرور ناموفق بود");
    }
  },

  /** لیست همه‌ی خریدهای کاربر (آرایه) — async */
  async getUserPurchases(userId) {
    if (!userId) return [];
    return await fetchAllFromServer(userId);
  },

  /** حذف یک خرید (برای تست/دیباگ) — async */
  async clearPurchase(userId, jozveId) {
    if (!userId || !jozveId) return false;
    const found = await fetchOneFromServer(userId, jozveId);
    if (!found) return false;
    try {
      await fetch(`${API_BASE}/jozveLicenses/${found.id}`, { method: 'DELETE' });
      return true;
    } catch (e) {
      console.warn("⚠️ jozveService: خطا در حذف:", e);
      return false;
    }
  },

  /** حذف همه‌ی خریدهای یک کاربر (برای تست) — async */
  async clearAll(userId) {
    if (!userId) return;
    try {
      const all = await fetchAllFromServer(userId);
      await Promise.all(
        all.map((item) =>
          fetch(`${API_BASE}/jozveLicenses/${item.id}`, { method: 'DELETE' }).catch(() => {})
        )
      );
    } catch (e) {
      console.warn("⚠️ jozveService: خطا در حذف همه:", e);
    }
  },
  // 🆕 ذخیره یادداشت‌های خط‌کشی جزوه در سرور
  async saveAnnotations(userId, jozveId, pageNumber, strokes) {
    try {
      const res = await fetch(`/api/db/jozweAnnotations?userId=${userId}&jozveId=${jozveId}&pageNumber=${pageNumber}`);
      const data = await res.json();
      const existing = Array.isArray(data) ? data[0] : null;

      const payload = { userId, jozveId, pageNumber, strokes, updatedAt: new Date().toISOString() };

      if (existing) {
        await fetch(`/api/db/jozweAnnotations/${existing.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
      } else {
        await fetch(`/api/db/jozweAnnotations`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
      }
    } catch (e) {
      console.warn('خطا در ذخیره آنوتیشن جزوه:', e);
    }
  },

  // 🆕 خواندن یادداشت‌های خط‌کشی جزوه از سرور
  async loadAnnotations(userId, jozveId) {
    try {
      const res = await fetch(`/api/db/jozweAnnotations?userId=${userId}&jozveId=${jozveId}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    } catch (e) {
      console.warn('خطا در خواندن آنوتیشن جزوه:', e);
      return [];
    }
  }
};

export default jozveService;